import { createSlice } from '@reduxjs/toolkit';
import {
    getClientSettings,
    createOrganisationContact,
    updateSettings,
    updateOrganisationalImage as updateOrganisationalImageApi,
    getCurrentSubscription,
    getSubscriptionList,
    editNotification as editNotificationApi,
    updateDirectLinksStatus as updateDirectLinksStatusApi,
    updateDefaultQuizLevel as updateDefaultQuizLevelApi,
    updateAutoIncludeCourseReady as updateAutoIncludeCourseReadyApi,
    editThreshold as editThresholdApi,
    updateFeatureSettings as updateFeatureSettingsApi
} from 'api/settings';
import { dispatch } from '../index';

const initialState = {
    clientSettings: {},
    clientSettingsStatus: 'idle',
    subscription: {
        list: [],
        total: 0,
        isFetching: true,
        params: {
            query: '',
            pageNo: 1,
            perPage: 10,
            sortBy: 'createdAt',
            sortDirection: 'DESC'
        },
        currentPlan: {
            data: {},
            isFetching: true
        }
    }
};

const slice = createSlice({
    name: 'settings',
    initialState,
    reducers: {
        setClientSettings(state, action) {
            state.clientSettings = action.payload.data;
            state.clientSettingsStatus = 'succeeded';
        },
        setClientSettingsStatus(state, action) {
            state.clientSettingsStatus = action.payload;
        },
        // SET Params dynamically base on key
        setParams(state, action) {
            const { params, key } = action.payload;
            const item = state[key];
            if (item) {
                item.params = params;
            }
        },
        // SET IsFetching dynamically base on key
        setIsFetching(state, action) {
            const { status, key } = action.payload;
            const item = state[key];
            if (item) {
                item.isFetching = status;
            }
        },
        // SET List dynamically base on key
        setList(state, action) {
            const { data, isUpdateTotalCount, key } = action.payload;
            const { list = [], summary } = data;
            const item = state[key];
            if (item) {
                item.list = list;
                // we will update the count
                if ((item.total === 0 || isUpdateTotalCount) && summary) {
                    const { total = 0 } = summary;
                    item.total = total;
                }
            }
        },
        // SET Current Plan for Subscription
        setCurrentPlan(state, action) {
            const item = state.subscription;
            if (item) {
                const { data = {} } = action.payload;
                item.currentPlan.data = data;
            }
        },
        // SET Current Plan is Fetching for Subscription
        setCurrentPlanIsFetching(state, action) {
            const item = state.subscription;
            if (item) {
                item.currentPlan.isFetching = action.payload;
            }
        }
    }
});

export const { setClientSettings, setParams, setIsFetching, setCurrentPlanIsFetching, setList } = slice.actions;
export default slice.reducer;

export const fetchClientSettings = () => async (dispatch) => {
    dispatch(slice.actions.setClientSettingsStatus('loading'));
    try {
        const response = await getClientSettings();
        if (!response?.data || Object.keys(response.data).length === 0) {
            throw new Error('Organisation settings are unavailable');
        }
        dispatch(setClientSettings({ data: response.data }));
        return response.data;
    } catch (error) {
        dispatch(slice.actions.setClientSettingsStatus('failed'));
        throw error;
    }
};

export const updateOrganisationalSettings = (data) => async (dispatch) => {
    const response = await updateSettings(data);
    if (response?.data) {
        dispatch(setClientSettings({ data: response.data }));
    }
};

export const addOrganisationContact = (data) => async (dispatch) => {
    const response = await createOrganisationContact(data);
    if (response?.data) {
        dispatch(setClientSettings({ data: response.data }));
    }
    return response;
};

export const updateNotificationSettings = (data) => async (dispatch) => {
    const response = await editNotificationApi(data);
    if (response?.data) {
        dispatch(setClientSettings({ data: response.data }));
    }
};

export const updateDefaultQuizLevel = (data) => async (dispatch) => {
    const response = await updateDefaultQuizLevelApi(data);
    if (response?.data) {
        dispatch(setClientSettings({ data: response.data }));
    }
};

export const updateAutoIncludeCourseReady = (data) => async (dispatch) => {
    const response = await updateAutoIncludeCourseReadyApi(data);
    if (response?.data) {
        dispatch(setClientSettings({ data: response.data }));
    }
};

export const updateOrganisationalImage = (formData) => async (dispatch) => {
    const response = await updateOrganisationalImageApi(formData);
    if (response?.data) {
        dispatch(setClientSettings({ data: response.data }));
    }
};

export const updateDirectLinksStatus = (data) => async (dispatch) => {
    const response = await updateDirectLinksStatusApi(data);
    if (response?.data) {
        dispatch(setClientSettings({ data: response.data }));
    }
};

export function fetchSubscriptions(params, isUpdateTotalCount = false) {
    return async () => {
        const key = 'subscription';
        try {
            const result = await getSubscriptionList(params);
            dispatch(
                slice.actions.setList({
                    data: result?.data || [],
                    isUpdateTotalCount,
                    key
                })
            );
        } catch (error) {
            console.log('error', error);
            dispatch(
                slice.actions.setList({
                    data: [],
                    key
                })
            );
        }
    };
}

export function fetchCurrentPlan() {
    return async () => {
        try {
            const result = await getCurrentSubscription();
            dispatch(slice.actions.setCurrentPlan({ data: result?.data || {} }));
        } catch (error) {
            console.log('error', error);
            dispatch(slice.actions.setCurrentPlan({ data: {} }));
        }
    };
}

export const updateThresholdSettings = (data) => async (dispatch) => {
    const response = await editThresholdApi(data);
    if (response?.data) {
        dispatch(setClientSettings({ data: response.data }));
    }
};

export const updateFeatureSettings = (data) => async (dispatch) => {
    const response = await updateFeatureSettingsApi(data);
    if (response?.data) {
        dispatch(setClientSettings({ data: response.data }));
    }
    return response;
};
